import { Select, Spin } from 'antd';
import { useMemo } from 'react';

import type { RecordClientSearchResult } from '@app/components/RecordModal/components/RecordDetails/types';
import { ClientSearchOption } from '@app/components/RecordModal/components/RecordDetails/ui/ClientSearchOption';
import { WithTooltip } from '@app/components/WithTooltip';

import type { DefaultOptionType } from 'antd/es/select';
import type { FC } from 'react';

import '@app/components/RecordModal/components/RecordDetails/styles/ClientSearch.scss';

interface ClientSearchSelectOption extends DefaultOptionType {
  client: RecordClientSearchResult;
  label: string;
  value: number;
}

interface ClientSearchProps {
  searchValue: string;
  clients: RecordClientSearchResult[];
  clientId: number | null;
  clientName: string | null;
  isLoading: boolean;
  isSuccess: boolean;
  isTooShort: boolean;
  disabled?: boolean;
  onSearch(value: string): void;
  onSelect(value: number): void;
  /** Почему клиента сменить нельзя: текст подсказки у выключенного поля */
  lockReason?: string | null;
}

export const ClientSearch: FC<ClientSearchProps> = ({
  searchValue,
  clients,
  clientId,
  clientName,
  isLoading,
  isSuccess,
  isTooShort,
  disabled = false,
  onSearch,
  onSelect,
  lockReason = null,
}) => {
  const options = useMemo<ClientSearchSelectOption[]>(
    () =>
      clients.map((client) => {
        const fullName = `${client.NameLast || ''} ${
          client.NameFirst || ''
        }`.trim();

        return {
          client,
          label: fullName || `Клиент #${client.Id}`,
          value: client.Id,
        };
      }),
    [clients]
  );

  const notFoundContent = useMemo(() => {
    if (isLoading) return <Spin size="small" />;
    if (isTooShort) return 'Введите минимум 3 символа';
    if (isSuccess) return 'Клиенты не найдены';

    return null;
  }, [isLoading, isSuccess, isTooShort]);

  const searchSelect = (
    <Select<number, ClientSearchSelectOption>
      className="ClientSearch"
      size="large"
      showSearch
      filterOption={false}
      placeholder="Введите имя или телефон"
      value={clientId ?? undefined}
      searchValue={searchValue}
      options={options}
      loading={isLoading}
      disabled={disabled}
      notFoundContent={notFoundContent}
      onSearch={onSearch}
      onSelect={onSelect}
      labelRender={({ label }) => clientName || label}
      optionRender={({ data: { client } }) => (
        <ClientSearchOption client={client} />
      )}
      data-testid="client-search"
    />
  );

  return (
    <WithTooltip show={!!lockReason} title={lockReason}>
      {searchSelect}
    </WithTooltip>
  );
};
